import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Drawer } from "@/components/ui/Drawer";
import type { TabItem } from "@/components/ui/TabGroup";
import { ChevronLeftIcon, MenuIcon } from "@/components/icons";
import { cn } from "@/utils/cn";

interface PhoneNavSheetProps {
  /** The sheet's heading: "Menu" for the agency, the client's name in a workspace. */
  title: string;
  /** The header's own tab list, so labels and icons stay those of TabGroup. */
  items: TabItem[];
  activeId: string;
  onSelect: (id: string) => void;
  /** First row, leading out of the area (the workspace's way back to the agency). */
  backLink?: { label: string; to: string };
}

const ROW = "w-full flex items-center gap-3 px-3 py-3 rounded-lg text-base text-left transition-colors";

/**
 * Phone navigation (DD-057): below md a header's tab strip gives way to this
 * burger, which opens the shared Drawer's bottom sheet listing every section.
 * From md up the burger hides and the header shows its tab strip.
 */
export const PhoneNavSheet: React.FC<PhoneNavSheetProps> = ({ title, items, activeId, onSelect, backLink }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Apri menu"
        aria-haspopup="dialog"
        aria-expanded={open}
        className="md:hidden p-2 -ml-2 text-gray-600 hover:text-gray-900 transition-colors shrink-0"
      >
        <MenuIcon className="w-5 h-5" />
      </button>

      {/* Sized to its rows, up to the sheet's usual cap. */}
      <Drawer isOpen={open} onClose={() => setOpen(false)} title={title} className="h-auto max-h-[90dvh]">
        <nav className="p-3">
          {backLink && (
            <Link
              to={backLink.to}
              onClick={() => setOpen(false)}
              className={cn(ROW, "font-medium text-gray-600 hover:bg-gray-100")}
            >
              <ChevronLeftIcon className="w-4 h-4" />
              {backLink.label}
            </Link>
          )}
          {items.map((item) => {
            const current = item.id === activeId;
            return (
              <button
                key={item.id}
                type="button"
                aria-current={current ? "page" : undefined}
                onClick={() => {
                  setOpen(false);
                  onSelect(item.id);
                }}
                className={cn(
                  ROW,
                  current ? "bg-gray-100 font-semibold text-gray-900" : "font-medium text-gray-700 hover:bg-gray-50",
                )}
              >
                {item.icon}
                {item.label}
              </button>
            );
          })}
        </nav>
      </Drawer>
    </>
  );
};
